import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { RupiahInput } from "@/components/RupiahInput";
import { FileUploader } from "@/components/FileUploader";
import { AdminDeleteButton } from "@/components/AdminActions";
import { SELECT_CLS, today } from "@/components/SalesShared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Banknote, Loader2, AlertTriangle, ArrowRightLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const accText = (a) => [a.bank_name, a.account_number, a.account_holder ? `a.n. ${a.account_holder}` : ""].filter(Boolean).join(" ");

export const sourceLabel = (s) => (!s ? "" : s.type === "cash" ? (s.pooled ? "Kas Tunai Pusat (gabungan)" : `Tunai Pusat ${s.company_name}`) : `${s.company_name} · ${accText(s)}`);

export function useFundSources(open) {
  const [list, setList] = useState(null);
  useEffect(() => {
    if (open) api.get("/fund-sources").then((r) => setList(r.data)).catch(() => setList([]));
  }, [open]);
  return list;
}

function Alert({ tone, children, testId }) {
  const cls = tone === "red" ? "border-rose-300 bg-rose-50 text-rose-800" : "border-amber-300 bg-amber-50 text-amber-800";
  return <div className={`flex gap-2 rounded-md border p-2 text-xs ${cls}`} data-testid={testId}><AlertTriangle className="h-4 w-4 shrink-0" /><span>{children}</span></div>;
}

export function FundSourcePicker({ sources, value, onChange, amount, lockedPt, bankDisabled }) {
  const cash = value.source_type === "cash";
  const pt = sources.find((s) => s.company_id === value.company_id);
  const acc = pt?.accounts.find((a) => a.account_number === value.account_number);
  const bal = cash ? (sources[0]?.pooled_cash_balance ?? 0) : acc?.balance;
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value, ...(k === "source_type" || k === "company_id" ? { account_number: "" } : {}) });
  return (
    <div className="space-y-3" data-testid="fund-source-picker">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Sumber Dana</Label>
          <select value={value.source_type} onChange={set("source_type")} className={`${SELECT_CLS} mt-1 h-9 w-full`} data-testid="fund-source-type">
            <option value="cash">Tunai Pusat</option><option value="bank" disabled={bankDisabled}>Transfer dari Rekening PT{bankDisabled ? " (pilih Pemohon 1 PT)" : ""}</option>
          </select></div>
        {cash ? (
          <div><Label className="text-xs">Kas</Label>
            <p className="mt-1 h-9 flex items-center rounded-md border bg-secondary/60 px-3 text-sm font-medium" data-testid="fund-source-pooled">Kas Tunai Pusat (gabungan)</p></div>
        ) : lockedPt ? (
          <div><Label className="text-xs">PT (dari Pemohon)</Label>
            <p className="mt-1 h-9 flex items-center rounded-md border bg-secondary/60 px-3 text-sm font-medium" data-testid="fund-source-pt-locked">{pt?.company_name || "-"}</p></div>
        ) : (
        <div><Label className="text-xs">PT <span className="font-normal text-amber-700" data-testid="fund-source-pt-missing">(Pemohon belum terhubung ke PT — atur di Kelola User)</span></Label>
          <select value={value.company_id} onChange={set("company_id")} className={`${SELECT_CLS} mt-1 h-9 w-full`} data-testid="fund-source-pt">
            <option value="">Pilih PT</option>
            {sources.map((s) => <option key={s.company_id} value={s.company_id}>{s.company_name}</option>)}
          </select></div>
        )}
      </div>
      {value.source_type === "bank" && pt && (
        <div><Label className="text-xs">Rekening</Label>
          <select value={value.account_number} onChange={set("account_number")} className={`${SELECT_CLS} mt-1 h-9 w-full`} data-testid="fund-source-account">
            <option value="">Pilih rekening</option>
            {pt.accounts.map((a) => <option key={a.account_number} value={a.account_number}>{accText(a)} — Saldo {formatRupiah(a.balance)}</option>)}
          </select></div>
      )}
      {bal != null && (
        <p className="text-xs text-muted-foreground" data-testid="fund-source-balance">Saldo {cash ? "Kas Tunai Pusat" : "rekening"} {formatRupiah(bal)} · setelah transaksi <span className="font-mono">{formatRupiah(bal - amount)}</span></p>
      )}
      {bal != null && bal < amount && (value.source_type === "cash"
        ? <Alert tone="red" testId="fund-source-cash-blocked">Saldo Tunai Pusat tidak mencukupi (kurang {formatRupiah(amount - bal)}). Pilih sumber dana lain.</Alert>
        : <Alert testId="fund-source-bank-warning">Saldo rekening tidak mencukupi (kurang {formatRupiah(amount - bal)}). Tetap bisa diproses, saldo menjadi minus.</Alert>)}
    </div>
  );
}

const sourceReady = (sources, v, amount) => {
  if (!sources?.length) return true;
  if (v.source_type === "cash") return (sources[0]?.pooled_cash_balance ?? 0) + 0.001 >= amount;
  const pt = sources.find((s) => s.company_id === v.company_id);
  return !!pt && !!v.account_number;
};

export function DisburseDialog({ req, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({ source_type: "cash", company_id: "", account_number: "" });
  const [busy, setBusy] = useState(false);
  const sources = useFundSources(open);
  const amount = req.approved_amount || 0;
  const lockedPt = req.requester_company_id || "";
  const init = { source_type: "cash", company_id: lockedPt, account_number: "" };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/cash-requests/${req.id}/disburse`, sources?.length ? v : {});
      toast.success("Kas berhasil dicairkan."); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setV(init); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`disburse-dialog-${req.id}`}>
        <DialogHeader>
          <DialogTitle>Cairkan Kas</DialogTitle>
          <DialogDescription>Kas <strong>{formatRupiah(amount)}</strong> untuk <strong>{req.requester_name}</strong>. Pilih sumber dana pencairan.</DialogDescription>
        </DialogHeader>
        {!sources ? <Loader2 className="h-5 w-5 animate-spin text-brand mx-auto" /> : sources.length === 0
          ? <p className="text-xs text-muted-foreground">Belum ada data PT — pencairan tanpa sumber dana.</p>
          : <FundSourcePicker sources={sources} value={v} onChange={setV} amount={amount} lockedPt={lockedPt} />}
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !sources || !sourceReady(sources, v, amount)} className="bg-brand hover:bg-emerald-800" data-testid={`confirm-disburse-${req.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Banknote className="h-4 w-4 mr-1.5" /> Cairkan Kas</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkDisburseDialog({ reqs, onDone }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({ source_type: "cash", company_id: "", account_number: "" });
  const [busy, setBusy] = useState(false);
  const sources = useFundSources(open);
  const amount = reqs.reduce((t, r) => t + (r.approved_amount || 0), 0);
  const groups = reqs.reduce((m, r) => { const k = r.requester_company_id || ""; m[k] = (m[k] || 0) + (r.approved_amount || 0); return m; }, {});
  const keys = Object.keys(groups);
  const single = keys.length === 1 && keys[0] ? keys[0] : "";
  const mixed = keys.length > 1 || (keys.length === 1 && !keys[0]);
  const init = { source_type: "cash", company_id: single, account_number: "" };
  const ready = !sources?.length || (sourceReady(sources, v, amount) && !(mixed && v.source_type === "bank"));
  const submit = async () => {
    setBusy(true);
    try {
      const body = !sources?.length ? {} : v;
      const r = await api.post("/cash-requests/bulk-disburse", { ids: reqs.map((x) => x.id), ...body });
      toast.success(`${r.data.count} pengajuan berhasil dicairkan (${formatRupiah(r.data.total)}).`); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setV(init); }}>
      <DialogTrigger asChild>
        <Button disabled={!reqs.length} className="bg-brand hover:bg-emerald-800" data-testid="bulk-disburse-btn"><Banknote className="h-4 w-4 mr-1.5" /> Cairkan Terpilih ({reqs.length})</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-disburse-dialog">
        <DialogHeader>
          <DialogTitle>Cairkan {reqs.length} Pengajuan Sekaligus</DialogTitle>
          <DialogDescription>Total <strong data-testid="bulk-disburse-total">{formatRupiah(amount)}</strong> dari satu sumber dana.</DialogDescription>
        </DialogHeader>
        <div className="max-h-40 overflow-y-auto rounded-md border divide-y text-xs">
          {reqs.map((r) => <div key={r.id} className="flex justify-between gap-2 px-3 py-1.5"><span className="truncate">{r.requester_name} · {r.divisi}</span><span className="font-mono">{formatRupiah(r.approved_amount)}</span></div>)}
        </div>
        {!sources ? <Loader2 className="h-5 w-5 animate-spin text-brand mx-auto" /> : sources.length === 0
          ? <p className="text-xs text-muted-foreground">Belum ada data PT — pencairan tanpa sumber dana.</p>
          : <FundSourcePicker sources={sources} value={v} onChange={setV} amount={amount} lockedPt={single} bankDisabled={mixed} />}
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !sources || !reqs.length || !ready} className="bg-brand hover:bg-emerald-800" data-testid="bulk-disburse-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Banknote className="h-4 w-4 mr-1.5" /> Cairkan Semua</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_TR = { from_company_id: "", to_company_id: "", account_number: "", amount: "", date: "", note: "" };

export function CashTransferDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY_TR);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const sources = useFundSources(open) || [];
  const from = sources.find((s) => s.company_id === f.from_company_id);
  const to = sources.find((s) => s.company_id === f.to_company_id);
  const amt = Number(f.amount) || 0;
  const pooledBal = sources[0]?.pooled_cash_balance ?? 0;
  const short = from && amt > pooledBal + 0.001;
  const set = (k) => (e) => setF((p) => ({
    ...p, [k]: e.target.value,
    ...(k === "from_company_id" && (!p.to_company_id || p.to_company_id === p.from_company_id) ? { to_company_id: e.target.value, account_number: "" } : {}),
    ...(k === "to_company_id" ? { account_number: "" } : {}),
  }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/cash-transfers", { ...f, amount: amt, file_id: files[0]?.file_id || null });
      toast.success("Setoran Tunai Pusat ke rekening tercatat."); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setF({ ...EMPTY_TR, date: today() }); setFiles([]); } }}>
      <DialogTrigger asChild>
        <Button className="bg-brand hover:bg-emerald-800" data-testid="cash-transfer-btn"><ArrowRightLeft className="h-4 w-4 mr-1.5" /> Setor Tunai Pusat ke Rekening</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cash-transfer-dialog">
        <DialogHeader><DialogTitle>Setor Tunai Pusat ke Rekening</DialogTitle>
          <DialogDescription>Tunai Pusat PT asal berkurang dan dana masuk ke mutasi rekening tujuan.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs">Tunai Pusat PT Asal</Label>
            <select value={f.from_company_id} onChange={set("from_company_id")} required className={`${SELECT_CLS} mt-1 h-9 w-full`} data-testid="cash-transfer-from">
              <option value="">Pilih PT</option>{sources.map((s) => <option key={s.company_id} value={s.company_id}>{s.company_name}</option>)}
            </select></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">PT Tujuan</Label>
              <select value={f.to_company_id} onChange={set("to_company_id")} required className={`${SELECT_CLS} mt-1 h-9 w-full`} data-testid="cash-transfer-to-pt">
                <option value="">Pilih PT</option>{sources.map((s) => <option key={s.company_id} value={s.company_id}>{s.company_name}</option>)}
              </select></div>
            <div><Label className="text-xs">Rekening Tujuan</Label>
              <select value={f.account_number} onChange={set("account_number")} required className={`${SELECT_CLS} mt-1 h-9 w-full`} data-testid="cash-transfer-account">
                <option value="">Pilih rekening</option>{(to?.accounts || []).map((a) => <option key={a.account_number} value={a.account_number}>{accText(a)}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Nominal</Label><RupiahInput value={f.amount} onChange={set("amount")} required className="mt-1 font-mono" data-testid="cash-transfer-amount" /></div>
            <div><Label className="text-xs">Tanggal</Label><Input type="date" value={f.date} onChange={set("date")} required className="mt-1 h-9" data-testid="cash-transfer-date" /></div>
          </div>
          {from && <p className="text-xs text-muted-foreground" data-testid="cash-transfer-balance">Saldo Kas Tunai Pusat (gabungan) {formatRupiah(pooledBal)} · setelah setor {formatRupiah(pooledBal - amt)}</p>}
          {short && <Alert tone="red" testId="cash-transfer-blocked">Saldo Kas Tunai Pusat tidak mencukupi (kurang {formatRupiah(amt - pooledBal)}).</Alert>}
          <div><Label className="text-xs">Catatan (opsional)</Label><Input value={f.note} onChange={set("note")} className="mt-1" data-testid="cash-transfer-note" /></div>
          <div><Label className="text-xs">Bukti Setor (opsional)</Label><div className="mt-1"><FileUploader files={files} onChange={setFiles} multiple={false} testId="cash-transfer-uploader" /></div></div>
          <DialogFooter>
            <Button type="submit" disabled={busy || short || amt <= 0} className="bg-brand hover:bg-emerald-800" data-testid="cash-transfer-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Setoran"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CashTransferList({ rows, isAdmin, onDone }) {
  if (!rows?.length) return null;
  return (
    <Card className="p-0 overflow-hidden" data-testid="cash-transfer-list">
      <p className="px-4 py-3 font-semibold text-sm border-b" style={{ fontFamily: "Outfit" }}>Riwayat Setor Tunai Pusat ke Rekening</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-secondary text-muted-foreground"><tr><th className="text-left px-4 py-2">Tanggal</th><th className="text-left px-4 py-2">Dari</th><th className="text-left px-4 py-2">Ke Rekening</th><th className="text-right px-4 py-2">Nominal</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-t align-top" data-testid={`cash-transfer-row-${t.id}`}>
                <td className="px-4 py-2 whitespace-nowrap">{formatDate(t.date)}</td>
                <td className="px-4 py-2">Tunai Pusat {t.from_company_name}<span className="block text-muted-foreground">{t.created_by_name}{t.note ? ` · ${t.note}` : ""}</span></td>
                <td className="px-4 py-2">{t.to_company_name}<span className="block font-mono text-muted-foreground">{accText(t.target_account || {})}</span></td>
                <td className="px-4 py-2 text-right font-mono">{formatRupiah(t.amount)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {t.file_id && <a href={fileUrl(t.file_id)} target="_blank" rel="noreferrer" className="text-brand inline-flex items-center gap-1 mr-2">Bukti <ExternalLink className="h-3 w-3" /></a>}
                  {isAdmin && <AdminDeleteButton path={`/cash-transfers/${t.id}`} onDone={onDone} iconOnly testId={`cash-transfer-del-${t.id}`} confirmText="Setoran dihapus permanen. Tunai Pusat kembali bertambah dan dana hilang dari mutasi rekening tujuan." />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
